'use client';

import { useNativeData } from '@onyx/help-hub/hooks';
import type { MenuItem } from '@onyx/ui/components/colleague/menu';
import { Menu } from '@onyx/ui/components/colleague/menu';
import { usePathname, useRouter } from 'next/navigation';

import { signOut, useSession } from '../auth/next-auth-client';
import { selectDraftIssueWithStore, useStore } from '../state/store-provider';
import { useTeamsContext } from '../utils/teams-context';
import { getInitials } from '../utils/utils';
import { ChatbotEntryBar } from './chatbot-entry-bar/chatbot-entry-bar';
import styles from './header.module.css';
import { HeaderInfoBadge } from './header-info-badge/header-info-badge';

type HeaderProps = {
  hasSearchBar?: boolean;
};

enum MenuItemId {
  Logout = 'logout',
  ChangeStore = 'changeStore',
}

export const Header = ({ hasSearchBar }: HeaderProps) => {
  const { enabled: isNativeDataEnabled } = useNativeData();
  const { isInTeams, isInitialized: isTeamsInitialized } = useTeamsContext();
  const { data: session } = useSession();
  const loggedInUser = session?.user;
  const initials = loggedInUser?.name ? getInitials(loggedInUser.name) : '';
  const router = useRouter();
  const pathname = usePathname();

  const draftIssue = useStore(selectDraftIssueWithStore);
  const { store } = draftIssue;

  const baseMenuItems = [
    {
      id: MenuItemId.ChangeStore,
      title: 'Change store',
      isSelected: false,
    },
  ];

  const menuItems = [
    ...baseMenuItems,
    {
      id: MenuItemId.Logout,
      title: 'Log out',
      isSelected: false,
    },
  ] satisfies MenuItem[];

  const handleMenuItemSelect = ({ id }: { id: string }) => {
    if (id === MenuItemId.Logout) {
      return void signOut();
    }

    return router.push(`/select-store?returnTo=${pathname}`);
  };

  // Wait for Teams detection to complete before deciding whether to expose the
  // log out option; otherwise the menu can briefly appear inside a Teams iframe
  // while `isInTeams` is still defaulting to `false`.
  const isLoggedOutEnabled =
    !isNativeDataEnabled && isTeamsInitialized && !isInTeams;

  return (
    <header>
      <div className={styles.container}>
        <div className={styles.titleContainer}>
          <div className={styles.inner}>
            <h1 className={styles.title}>Help</h1>
            <p className={styles.text}>
              Get support from the right people, or help finding the answers you
              need in The M&S Way.
            </p>
          </div>
          {!!loggedInUser && (
            <div className={styles.badgeContainer}>
              <Menu
                items={isLoggedOutEnabled ? menuItems : baseMenuItems}
                shouldCloseOnSelect
                onSelect={handleMenuItemSelect}
                trigger={
                  <HeaderInfoBadge
                    storeId={store?.id}
                    label={initials}
                    image={loggedInUser?.image ?? undefined}
                  />
                }
              />
            </div>
          )}
        </div>
      </div>
      {hasSearchBar && <ChatbotEntryBar placeholder="Ask about the M&S Way" />}
    </header>
  );
};
